import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, Send, Sparkles, Check, CheckCheck, Mail, Zap } from "lucide-react";

const TABS = [
  ["awaiting_approval", "Awaiting Approval"],
  ["draft", "Draft"],
  ["approved", "Approved"],
  ["sent", "Sent"],
  ["replied", "Replied"],
];

export default function GrowthOutreach() {
  const [tab, setTab] = useState("awaiting_approval");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [editingBody, setEditingBody] = useState({}); // id → body

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/growth/outreach", { params: { status: tab } });
      setItems(data.items || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const regenerate = async (o) => {
    setBusy(o.id);
    try {
      await api.post(`/growth/outreach/${o.id}/generate`);
      toast.success("AI redrafted the body.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(null); }
  };

  const approve = async (o) => {
    setBusy(o.id);
    try {
      await api.post(`/growth/outreach/${o.id}/approve`, { note: null });
      toast.success("Approved.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(null); }
  };

  const markSent = async (o) => {
    setBusy(o.id);
    try {
      await api.post(`/growth/outreach/${o.id}/mark-sent`, { note: null });
      toast.success("Marked sent.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(null); }
  };

  const sendViaResend = async (o) => {
    if (!window.confirm(`Send this email now to ${o.to_email}?`)) return;
    setBusy(o.id);
    try {
      const { data } = await api.post(`/growth/outreach/${o.id}/send`, { note: null });
      toast.success(`Sent to ${data.to}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(null); }
  };

  return (
    <div className="p-6 md:p-8 space-y-5 max-w-6xl mx-auto" data-testid="growth-outreach">
      <div>
        <h1 className="text-3xl font-extrabold text-white tracking-tight flex items-center gap-2"><Send className="h-7 w-7 text-indigo-400" /> Outreach Center</h1>
        <p className="text-slate-400 text-sm mt-1">Draft quality is the single biggest lever on conversion. AI drafts; you approve.</p>
      </div>

      <div className="flex gap-1 flex-wrap border-b border-slate-800 pb-1">
        {TABS.map(([val, label]) => (
          <button key={val} onClick={() => setTab(val)} className={`px-3 py-1.5 text-sm font-bold rounded-t-md transition-colors ${tab === val ? "bg-indigo-500/15 text-white border-b-2 border-indigo-400" : "text-slate-400 hover:text-white"}`} data-testid={`tab-${val}`}>
            {label}
          </button>
        ))}
      </div>

      {loading ? <div className="text-slate-400"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</div>
      : items.length === 0 ? (
        <Card className="bg-slate-900/60 border-slate-800"><CardContent className="p-10 text-center text-slate-400 text-sm">No {tab.replace(/_/g, " ")} drafts.</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {items.map((o) => (
            <Card key={o.id} className="bg-slate-900/60 border-slate-800" data-testid={`outreach-row-${o.id}`}>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs text-slate-500">
                      {o.channel.toUpperCase()}
                      {o.category === "activation" && o.pattern && <span className="ml-2 text-[10px] font-extrabold uppercase tracking-widest text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded px-1.5 py-0.5">activation · {o.pattern.replace(/_/g, " ")}</span>}
                      {o.lead_id ? ` · lead ${o.lead_id.slice(0, 6)}` : ""}
                      {o.to_email ? ` · to ${o.to_email}` : ""}
                      {o.ai_confidence ? ` · AI ${Math.round(o.ai_confidence * 100)}%` : ""}
                    </p>
                    {o.subject && <p className="text-white font-bold mt-1">{o.subject}</p>}
                  </div>
                  <div className="flex items-center gap-1">
                    {o.opportunity_id && (
                      <Button size="sm" variant="outline" onClick={() => regenerate(o)} disabled={busy === o.id} className="border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700 h-7 text-xs" data-testid={`regen-${o.id}`}>
                        <Sparkles className="h-3 w-3 mr-1" /> Redraft
                      </Button>
                    )}
                    {o.status === "awaiting_approval" && (
                      <Button size="sm" onClick={() => approve(o)} disabled={busy === o.id} className="bg-emerald-600 hover:bg-emerald-500 h-7 text-xs" data-testid={`approve-${o.id}`}>
                        <Check className="h-3 w-3 mr-1" /> Approve
                      </Button>
                    )}
                    {(o.status === "approved" || (o.status === "awaiting_approval" && o.to_email)) && o.to_email && (
                      <Button size="sm" onClick={() => sendViaResend(o)} disabled={busy === o.id} className="bg-emerald-600 hover:bg-emerald-500 h-7 text-xs" data-testid={`send-resend-${o.id}`}>
                        <Zap className="h-3 w-3 mr-1" /> Send now
                      </Button>
                    )}
                    {o.status === "approved" && (
                      <Button size="sm" onClick={() => markSent(o)} disabled={busy === o.id} className="bg-indigo-600 hover:bg-indigo-500 h-7 text-xs" data-testid={`mark-sent-${o.id}`}>
                        <CheckCheck className="h-3 w-3 mr-1" /> Mark sent
                      </Button>
                    )}
                  </div>
                </div>
                <Textarea
                  rows={6}
                  value={editingBody[o.id] ?? o.body}
                  onChange={(e) => setEditingBody({ ...editingBody, [o.id]: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-slate-100 text-sm font-mono"
                  data-testid={`body-${o.id}`}
                />
                {o.personalization_evidence && (
                  <p className="text-xs text-slate-500 italic">Personalized from: "{o.personalization_evidence}"</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
